package com.example.hukakmp.data

import com.example.hukakmp.getPlatform
import com.example.hukakmp.model.Captura
import com.example.hukakmp.model.FirebaseResult
import com.example.hukakmp.model.ModalidadPesca
import com.example.hukakmp.model.NuevoParteInput
import com.example.hukakmp.model.PartePesca
import com.example.hukakmp.model.UbicacionParte
import com.example.hukakmp.util.calcularDuracion
import com.example.hukakmp.util.generarIdParte
import com.example.hukakmp.util.normalizarFecha
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.storage.StorageReference
import dev.gitlive.firebase.storage.storage
import dev.gitlive.firebase.storage.storageMetadata

private const val PARTES_COLLECTION = "partes_pesca"
private const val SUBCOLLECTION_PARTES = "partes"
private const val MAX_PHOTO_BYTES = 10 * 1024 * 1024
private val ALLOWED_PHOTO_CONTENT_TYPES = setOf(
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
)

private fun DocumentSnapshot.readStringList(field: String): List<String> {
    if (!contains(field)) return emptyList()
    return runCatching { get<List<String>>(field) }.getOrDefault(emptyList())
}

private fun DocumentSnapshot.readString(field: String): String? {
    if (!contains(field)) return null
    return runCatching { get<String>(field) }.getOrNull()
}

private fun webCompatiblePhotoUrls(
    document: DocumentSnapshot,
    parsedUrls: List<String>,
): List<String> {
    if (getPlatform().name != "Web") return parsedUrls

    return buildList {
        addAll(parsedUrls)

        listOf("fotos", "imagenes", "photos").forEach { field ->
            addAll(document.readStringList(field))
        }

        listOf("foto", "fotoUrl", "photoUrl", "photoUri").forEach { field ->
            document.readString(field)?.let(::add)
        }
    }
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .distinct()
}

class FishingRepository {
    private val firestore = Firebase.firestore
    private val storage = Firebase.storage
    private val pescadexRepository = PescadexRepository()
    private val tournamentRepository = TournamentRepository()

    suspend fun obtenerMisPartes(limite: Int = 50): List<PartePesca> {
        val userId = Firebase.auth.currentUser?.uid ?: return emptyList()
        return try {
            val snapshot = firestore
                .collection("$PARTES_COLLECTION/$userId/$SUBCOLLECTION_PARTES")
                .orderBy("timestamp", Direction.DESCENDING)
                .limit(limite)
                .get()

            snapshot.documents.mapNotNull { document ->
                try {
                    val parsed = document.data<PartePesca>().copy(id = document.id)
                    parsed.copy(
                        fotos = webCompatiblePhotoUrls(
                            document = document,
                            parsedUrls = parsed.fotos,
                        ),
                    )
                } catch (_: Exception) {
                    null
                }
            }
        } catch (_: Exception) {
            emptyList()
        }
    }

    suspend fun eliminarParte(id: String): FirebaseResult {
        val userId = Firebase.auth.currentUser?.uid
            ?: return FirebaseResult.Error("Usuario no autenticado")
        return try {
            firestore.document("$PARTES_COLLECTION/$userId/$SUBCOLLECTION_PARTES/$id").delete()
            FirebaseResult.Success
        } catch (e: Exception) {
            FirebaseResult.Error(e.message ?: "Error eliminando parte", e)
        }
    }

    suspend fun guardarParte(input: NuevoParteInput): FirebaseResult {
        val userId = Firebase.auth.currentUser?.uid
            ?: return FirebaseResult.Error("Usuario no autenticado")

        val fecha = normalizarFecha(input.fecha)
            ?: return FirebaseResult.Error("La fecha del parte no es válida")
        val modalidad = input.modalidad
            ?: return FirebaseResult.Error("Seleccioná una modalidad de pesca")
        if (input.fotos.isEmpty()) return FirebaseResult.Error("El parte debe incluir al menos una fotografía")

        input.fotos.forEach { foto ->
            val contentType = foto.contentType.trim().lowercase()
            if (contentType !in ALLOWED_PHOTO_CONTENT_TYPES) {
                return FirebaseResult.Error(
                    "Formato de imagen no permitido. Usá JPG, PNG, WebP, HEIC o HEIF."
                )
            }
            if (foto.bytes.isEmpty()) {
                return FirebaseResult.Error("Una de las fotografías está vacía.")
            }
            if (foto.bytes.size > MAX_PHOTO_BYTES) {
                return FirebaseResult.Error(
                    "Cada fotografía debe pesar como máximo 10 MB."
                )
            }
        }

        val latitud = input.latitud.trim().replace(',', '.').toDoubleOrNull()
            ?: return FirebaseResult.Error("La ubicación del parte no es válida")
        val longitud = input.longitud.trim().replace(',', '.').toDoubleOrNull()
            ?: return FirebaseResult.Error("La ubicación del parte no es válida")
        if (latitud !in -90.0..90.0 || longitud !in -180.0..180.0) {
            return FirebaseResult.Error("La ubicación del parte está fuera de rango")
        }

        val capturas = mutableListOf<Captura>()
        input.especies.forEach { entrada ->
            if (entrada.especie.isBlank() || entrada.cantidad <= 0 || entrada.devueltos !in 0..entrada.cantidad) {
                return FirebaseResult.Error("Revisá los datos de las especies capturadas")
            }

            val nombreIngresado = entrada.especie.trim()
            val nombreValidado = if (getPlatform().name == "Web") {
                PescadexCatalog.canonicalName(nombreIngresado)
                    ?: return FirebaseResult.Error(
                        "La especie \"$nombreIngresado\" no está en el catálogo de peces argentinos de Huka."
                    )
            } else {
                nombreIngresado
            }

            capturas += Captura(
                especie = nombreValidado,
                cantidad = entrada.cantidad,
                retenidos = entrada.cantidad - entrada.devueltos,
                devueltos = entrada.devueltos,
            )
        }

        val id = generarIdParte()
        val referenciasSubidas = mutableListOf<StorageReference>()

        return try {
            val fotosUrls = input.fotos.mapIndexed { index, foto ->
                val nombreSeguro = nombreArchivoSeguro(foto.nombre, index)
                val referencia = storage.reference.child(
                    "$PARTES_COLLECTION/$userId/$SUBCOLLECTION_PARTES/$id/fotos/$nombreSeguro"
                )
                referencia.putData(
                    storageData(foto.bytes),
                    storageMetadata { contentType = foto.contentType.trim().lowercase() },
                )
                referenciasSubidas += referencia
                referencia.getDownloadUrl()
            }

            val ubicacion = UbicacionParte(
                nombre = input.nombreLugar.trim().ifBlank { null },
                zona = input.zona.trim().ifBlank { null },
                latitud = latitud,
                longitud = longitud,
            )

            val tipo = when (modalidad) {
                ModalidadPesca.OTRA -> input.modalidadOtra.trim()
                else -> modalidad.displayName
            }

            val parte = PartePesca(
                id = id,
                userId = userId,
                fecha = fecha,
                horaInicio = input.horaInicio,
                horaFin = input.horaFin,
                duracionHoras = calcularDuracion(input.horaInicio, input.horaFin),
                tipo = tipo,
                modalidadOtra = if (modalidad == ModalidadPesca.OTRA) input.modalidadOtra.trim() else null,
                numeroCanas = if (modalidad.usaCanas) input.numeroCanas else null,
                cantidadTotal = capturas.sumOf { it.cantidad },
                cantidadDevuelta = capturas.sumOf { it.devueltos },
                observaciones = input.observaciones.trim().ifBlank { null },
                ubicacion = ubicacion,
                peces = capturas,
                fotos = fotosUrls,
                timestamp = Timestamp.now(),
                estado = "completado",
            )

            firestore.document("$PARTES_COLLECTION/$userId/$SUBCOLLECTION_PARTES/$id")
                .set(parte, merge = true)

            try {
                pescadexRepository.registrarEspeciesDeParte(
                    especies = capturas.map { it.especie to it.cantidad },
                    locacion = input.nombreLugar.trim().ifBlank { input.zona.trim() }.ifBlank { null },
                    fechaParte = fecha,
                )
            } catch (_: Exception) {
                // El Pescadex es best-effort: un fallo aquí no invalida el parte ya guardado.
            }

            try {
                tournamentRepository.onReportSaved(parte)
            } catch (_: Exception) {
                // Torneos también es best-effort: el parte principal ya quedó guardado.
            }

            FirebaseResult.Success
        } catch (e: Exception) {
            referenciasSubidas.forEach { referencia ->
                try {
                    referencia.delete()
                } catch (_: Exception) {
                    // Limpieza best-effort: el error principal se devuelve al usuario.
                }
            }
            FirebaseResult.Error(e.message ?: "Error guardando el parte o subiendo sus fotografías", e)
        }
    }

    private fun nombreArchivoSeguro(nombreOriginal: String, index: Int): String {
        val limpio = nombreOriginal
            .substringAfterLast('/')
            .substringAfterLast('\\')
            .replace(Regex("[^A-Za-z0-9._-]"), "_")
            .take(80)
            .ifBlank { "foto_${index + 1}.jpg" }
        return "${index + 1}_$limpio"
    }
}
